import React from 'react'
import PropTypes from 'prop-types'
import Highlighter from 'react-highlight-words'
import styled from 'styled-components'
import isAfter from 'date-fns/isAfter'
import Name from './Name'
import ZoomableImage from './ZoomableImage'
import rarities from '../../../data/rarities'
import Artwork, { StyledArtwork } from '../Artwork'
import Rarity from '../Rarity'
import Tooltip from '../../general/Tooltip'
import translate from '../../../helper/translate'
import { desktop, mobile } from '../../../styles/media'
import { Number, Remark } from '../PrintList'
import LazyLoadIfNotPrerender from '../../image/LazyLoadedImage'
import RarityBar from '../RarityBar'
import WantsOnImage from '../../collection/WantsOnImage'
import Icon from '../../general/Icon'
import LanguageAbbr from './LanguageAbbr'
import Decoration from './Decoration'

const CardName = ({ alternateName, artworkNumber, artworkCategory, children, childrenShowDesktop, collectionDataLoading, copies, decoration, disableHover, disableLazyLoad, hasAnyHaves, hasAnyWants, hasTranslatedCardName, haves, havesInOtherSets, highlight, highlightFallback, image, imageSize, imageZoomable, imageIsPlaceholder, innerOnMouseEnter, innerOnMouseLeave, lastPrint, lastPrintSetNumber, name, rarityId, remarkType, setLanguage, showLongRarity, showRarity, showWantsIndicator, t, validFrom, wants }) => {
	const lastPrintDate = lastPrint ? new Date(lastPrint) : null
	const lastPrintIsFuture = lastPrintDate ? isAfter(lastPrintDate, new Date()) : null

	const highlightNormalized = highlight ? highlight.toLowerCase() : ''
	const textToHighlight = highlightFallback && !name.toLowerCase().includes(highlightNormalized) && highlightFallback.toLowerCase().includes(highlightNormalized) ? highlightFallback : name

	return (
		<Name>
			{showRarity && !showLongRarity && (
				<RarityBar rarityId={rarityId} />
			)}

			{showLongRarity && rarityId && (
				<Tooltip title={rarities[rarityId] ? rarities[rarityId].name : null}>
					<Rarity id={rarityId}>{(rarities[rarityId] && rarities[rarityId].abbr) || '?'}</Rarity>
				</Tooltip>
			)}

			<LazyLoadIfNotPrerender height={30} offset={100} disabled={disableLazyLoad}>
				<ImageInner>
					<ZoomableImage
						size={imageSize}
						hover={!disableHover}
						image={image}
						imageIsPlaceholder={imageIsPlaceholder}
						zoomable={imageZoomable}
					/>
					{showWantsIndicator && (
						<WantsOnImage>
							<Icon name="heart" />
						</WantsOnImage>
					)}
				</ImageInner>
			</LazyLoadIfNotPrerender>
			<Inner onMouseEnter={innerOnMouseEnter} onMouseLeave={innerOnMouseLeave}>
				<NameInnerTop>
					{hasTranslatedCardName && <Quote>&quot;</Quote>}
					{highlight && (
						<Highlighter
							highlightClassName="highlightedText"
							searchWords={highlight.split(' ')}
							autoEscape
							textToHighlight={textToHighlight}
						/>
					)}
					{!highlight && name}
					{hasTranslatedCardName && <Quote>&quot;</Quote>}
					{artworkNumber !== null && artworkNumber !== 1 && <Artwork number={artworkNumber} category={artworkCategory} />}
					{setLanguage && <LanguageAbbr language={setLanguage.language} />}
					{copies && <Copies> ({copies}x {t('general.included')})</Copies>}
					{alternateName && <AlternateName>{alternateName}</AlternateName>}
					{decoration && <Decoration decoration={decoration} />}
					{remarkType === 'nerf' && <RemarkType nerf><Icon name="trending-down" /></RemarkType>}
					{remarkType === 'buff' && <RemarkType buff><Icon name="trending-up" /></RemarkType>}
					{remarkType === 'new' && <RemarkType nerf><Remark red>{t('general.new')}</Remark></RemarkType>}
				</NameInnerTop>
				{children && (
					<NameInnerBottom childrenShowDesktop={childrenShowDesktop}>
						{children}
					</NameInnerBottom>
				)}
				{/*{lastPrint && (
					<LastPrint>
						{lastPrintSetNumber ? (
							<>{lastPrintSetNumber}</>
						) : null}
						{lastPrintSetNumber && lastPrintIsFuture && (
							<>
								&nbsp;&middot;&nbsp;
								<ReprintAnnounced>
									{t('general.reprintAnnounced')}
								</ReprintAnnounced>
							</>
						)}
						{!lastPrintSetNumber && lastPrintIsFuture && (
							<Unreleased>
								<Icon name="sparkles" /> {t('general.firstPrintOn', { firstPrint: day(lastPrintDate) })}
							</Unreleased>
						)}
					</LastPrint>
				)}*/}
			</Inner>
		</Name>
	)
}

CardName.propTypes = {
	artworkNumber: PropTypes.number,
	artworkCategory: PropTypes.string,
	collectionDataLoading: PropTypes.bool,
	children: PropTypes.any,
	childrenShowDesktop: PropTypes.bool,
	copies: PropTypes.number,
	decoration: PropTypes.object,
	disableHover: PropTypes.bool,
	disableLazyLoad: PropTypes.bool,
	hasAnyHaves: PropTypes.bool,
	hasAnyWants: PropTypes.bool,
	hasTranslatedCardName: PropTypes.bool,
	haves: PropTypes.number,
	havesInOtherSets: PropTypes.number,
	highlight: PropTypes.string,
	highlightFallback: PropTypes.string,
	image: PropTypes.string,
	imageIsPlaceholder: PropTypes.bool,
	innerOnMouseEnter: PropTypes.func,
	innerOnMouseLeave: PropTypes.func,
	imageSize: PropTypes.number,
	imageZoomable: PropTypes.bool,
	lastPrint: PropTypes.any,
	lastPrintSetNumber: PropTypes.string,
	name: PropTypes.any.isRequired,
	rarityId: PropTypes.number,
	remarkType: PropTypes.string,
	setLanguage: PropTypes.object,
	showLongRarity: PropTypes.bool,
	showRarity: PropTypes.bool,
	showWantsIndicator: PropTypes.bool,
	t: PropTypes.func.isRequired,
	validFrom: PropTypes.string,
	wants: PropTypes.number
}

CardName.defaultProps = {
	artworkNumber: null,
	artworkCategory: null,
	children: null,
	childrenShowDesktop: false,
	copies: null,
	collectionDataLoading: false,
	decoration: null,
	disableHover: false,
	disableLazyLoad: false,
	hasAnyHaves: false,
	hasAnyWants: false,
	hasTranslatedCardName: false,
	haves: null,
	havesInOtherSets: null,
	highlight: null,
	highlightFallback: null,
	image: '',
	imageIsPlaceholder: false,
	imageSize: 30,
	imageZoomable: false,
	innerOnMouseEnter: undefined,
	innerOnMouseLeave: undefined,
	lastPrint: null,
	lastPrintSetNumber: null,
	rarityId: null,
	remarkType: null,
	setLanguage: null,
	showLongRarity: false,
	showRarity: false,
	showWantsIndicator: false,
	validFrom: null,
	wants: null
}

const Quote = styled.span`
	color: ${props => props.theme.textVeryLight};
`

const Copies = styled.span`
	color: ${props => props.theme.textVeryLight};
	font-size: 0.9rem;

	${mobile`
		display: none;
	`}
`

const Inner = styled.div`
	display: flex;
	flex-direction: column;
	text-align: left;

	> div ${Name} {
		margin-left: 0.5rem;
	}

	${mobile`
		width: 100%;
	`}
`

export const NameInnerTop = styled.div`
	${StyledArtwork} {
		margin-left: 0.5rem;
	}
`

const WantsWrapper = styled.div`
	display: inline-block;

	${props => props.hasWants && `
		background: ${props.theme.yellowLight};
    	border-radius: 2px;
		padding: 0.15rem;
		width: fit-content;

		> svg {
			color: ${props.theme.id === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
			margin-left: 0.35rem;
			height: 12px;
			width: 12px;
		}
	`}
`

export const NameInnerBottom = styled.div`
	color: ${props => props.theme.textVeryLight};
	font-size: 0.8rem;
	margin-top: 0.15rem;

	align-items: center;
	display: flex;

	${Number} {
        font-weight: 500;
	}

	${desktop`
		${props => !props.childrenShowDesktop && `
			display: none;
		`}
	`}
`

const ImageInner = styled.div`
	position: relative;
`

const AlternateName = styled.span`
	color: ${props => props.theme.textVeryLight};

	${desktop`
		margin-left: 0.25rem;
	`}

	${mobile`
		display: block;
	`}
`

const RemarkType = styled.span`
	color: ${props => props.nerf ? props.theme.red : props.theme.green};
	margin-left: auto;

	height: 20px;

	align-items: center;
	display: inline-flex;
	gap: 0.25rem;

	${desktop`
		min-width: 20px;
	`}
`

const enhance = translate('general')

export default enhance(CardName)
